/**
 * Phishing Detection Model
 * Combines feature extraction with Random Forest classification
 */

import { RandomForestClassifier } from './randomForest.js';
import { featureExtractor } from './featureExtractor.js';
import { generateTrainingData, trainTestSplit } from './trainingData.js';

class PhishingDetectionModel {
  constructor() {
    this.model = null;
    this.isTraining = false;
    this.metrics = null;
  }

  /**
   * Train the model
   */
  async train(options = {}) {
    this.isTraining = true;

    try {
      console.log('Generating training data...');
      const { X, y } = generateTrainingData();

      console.log(`Total samples: ${X.length}`);
      console.log(`Phishing samples: ${y.filter(l => l === 1).length}`);
      console.log(`Legitimate samples: ${y.filter(l => l === 0).length}`);

      // Split data
      const { X_train, X_test, y_train, y_test } = trainTestSplit(X, y, 0.2);

      console.log('Training Random Forest model...');
      
      // Create and train model
      this.model = new RandomForestClassifier({
        nEstimators: options.nEstimators || 50,
        maxDepth: options.maxDepth || 10,
        minSamplesSplit: options.minSamplesSplit || 2
      });

      this.model.fit(X_train, y_train);

      // Evaluate model
      console.log('Evaluating model...');
      const trainAccuracy = this.model.score(X_train, y_train);
      const testAccuracy = this.model.score(X_test, y_test);

      // Calculate additional metrics
      const predictions = X_test.map(x => this.model.predict(x));
      const { precision, recall, f1Score } = this.calculateMetrics(y_test, predictions);

      this.metrics = {
        trainAccuracy: Math.round(trainAccuracy * 1000) / 10,
        testAccuracy: Math.round(testAccuracy * 1000) / 10,
        precision: Math.round(precision * 1000) / 10,
        recall: Math.round(recall * 1000) / 10,
        f1Score: Math.round(f1Score * 1000) / 10,
        trainSamples: X_train.length,
        testSamples: X_test.length
      };

      console.log('Training complete!');
      console.log(`Train Accuracy: ${this.metrics.trainAccuracy}%`);
      console.log(`Test Accuracy: ${this.metrics.testAccuracy}%`);
      console.log(`Precision: ${this.metrics.precision}%`);
      console.log(`Recall: ${this.metrics.recall}%`);
      console.log(`F1 Score: ${this.metrics.f1Score}%`);

      this.isTraining = false;
      return this.metrics;
    } catch (error) {
      this.isTraining = false;
      throw error;
    }
  }

  /**
   * Calculate precision, recall, and F1 score
   */
  calculateMetrics(yTrue, yPred) {
    let tp = 0, fp = 0, fn = 0, tn = 0;

    for (let i = 0; i < yTrue.length; i++) {
      if (yTrue[i] === 1 && yPred[i] === 1) tp++;
      else if (yTrue[i] === 0 && yPred[i] === 1) fp++;
      else if (yTrue[i] === 1 && yPred[i] === 0) fn++;
      else tn++;
    }

    const precision = tp / (tp + fp) || 0;
    const recall = tp / (tp + fn) || 0;
    const f1Score = 2 * (precision * recall) / (precision + recall) || 0;

    return { precision, recall, f1Score, tp, fp, fn, tn };
  }

  /**
   * Predict if a URL is phishing
   */
  predict(url) {
    if (!this.model) {
      throw new Error('Model not trained. Call train() first.');
    }

    const result = featureExtractor.extractFeatures(url);
    if (!result) {
      throw new Error('Invalid URL');
    }

    const prediction = this.model.predict(result.featureVector);
    const probability = this.model.predictProba(result.featureVector);

    // Get feature descriptions
    const featureDescriptions = featureExtractor.getFeatureDescriptions(result.features);

    return {
      url: result.metadata.url,
      isPhishing: prediction === 1,
      confidence: Math.round(Math.abs(probability - 0.5) * 200),
      probability: Math.round(probability * 100),
      classification: this.getClassification(probability),
      features: result.features,
      featureDescriptions,
      metadata: result.metadata
    };
  }

  /**
   * Get classification label based on probability
   */
  getClassification(probability) {
    if (probability >= 0.7) return 'phishing';
    if (probability >= 0.4) return 'suspicious';
    return 'safe';
  }

  /**
   * Export model to JSON
   */
  exportModel() {
    if (!this.model) {
      throw new Error('Model not trained');
    }
    return {
      model: this.model.toJSON(),
      metrics: this.metrics
    };
  }

  /**
   * Import model from JSON
   */
  importModel(data) {
    this.model = RandomForestClassifier.fromJSON(data.model);
    this.metrics = data.metrics;
  }
}

// Singleton instance
export const phishingModel = new PhishingDetectionModel();
